<template>
  <v-modal modal-class="form-submissions-modal" @open="handleOpen">
    <template :slot="FORM_SUBMISSIONS_HASH">
      <div class="form-submissions-modal__content">
        <template v-if="isLoading">
          <div class="d-flex justify-content-center py-5 mt-5">
            <v-loader />
          </div>
        </template>

        <template v-else>
          <div class="px-2 py-1">
            <div
              v-if="currentSubmission"
              class="d-flex mb-3 align-items-center"
            >
              <v-button
                v-if="
                  !wasOpenedDirectly &&
                    hasMultipleSubmissions &&
                    currentSubmission
                "
                ghost
                secondary
                class="form-submissions-modal__back p-0 mr-2"
                @click="() => (currentSubmission = null)"
              >
                <v-icon icon="arrow_back" size="1.3rem" />
              </v-button>

              <h2>{{ currentSubmission.form.title }} Results</h2>
            </div>

            <div v-else>
              <h2>{{ header }}</h2>
            </div>
          </div>

          <transition name="fade-out-left" mode="out-in">
            <template v-if="isLoadingSubmission">
              <div
                class="d-flex align-items-center justify-content-center py-5"
              >
                <v-loader />
              </div>
            </template>
            <template v-else-if="currentSubmission">
              <div
                key="current-submission"
                class="form-submissions-modal__submission"
              >
                <form-submission-table
                  v-model="submissionValues"
                  :submission="currentSubmission"
                  :groups="currentSubmission.form_groups"
                  :is-missable="isMissable"
                />

                <cta-row v-if="wasOpenedDirectly" overlay-modal white-bg>
                  <v-button block secondary @click="close">
                    Done
                  </v-button>
                </cta-row>
              </div>
            </template>

            <grouped-form-submissions
              v-else
              allow-single-mode
              :key="`submissions-list-${key}`"
              :form-id="formId"
              class="p-2"
              @choose="submission => chooseSubmission(submission.id)"
              @loaded="({ submissions }) => (this.submissions = submissions)"
            />
          </transition>
        </template>
      </div>
    </template>
  </v-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

import { timeAgo } from "@/lib/date-utils"
import { toHashList, removeFromHash, addToHash } from "@/lib/url-helpers"
import EventBus, { EVENTS } from "@/lib/event-bus"

import FormSubmissionTable from "@/components/FormSubmissionTable"
import GroupedFormSubmissions from "@/components/form-submission/GroupedFormSubmissions"

export const FORM_SUBMISSIONS_HASH = "#form-submissions"

export default {
  props: {
    header: { type: String, required: true },
    formId: { type: Number, required: true },
    isMissable: { type: Boolean, default: false }
  },

  components: {
    FormSubmissionTable,
    GroupedFormSubmissions,
    CtaRow: () => import("@/components/CtaRow")
  },

  data() {
    return {
      submissions: [],
      isLoading: false,
      key: 1,
      isLoadingSubmission: false,
      currentSubmission: null,
      wasOpenedDirectly: false,

      FORM_SUBMISSIONS_HASH
    }
  },

  mounted() {
    EventBus.$on(EVENTS.SHOW_FORM_SUBMISSION, this.showSubmission)
  },

  beforeDestroy() {
    EventBus.$off(EVENTS.SHOW_FORM_SUBMISSION, this.showSubmission)
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),

    hasMultipleSubmissions() {
      return this.submissions.length > 1
    },

    submissionValues: {
      get() {
        return this.currentSubmission?.values || {}
      },

      set(values) {
        this.currentSubmission.values = values

        this.updateSubmissionValues({
          formId: this.currentSubmission.form_id,
          formSubmissionId: this.currentSubmission.id,
          values: values
        })
      }
    }
  },

  methods: {
    timeAgo,

    ...mapActions("forms", ["fetchFormSubmission", "updateSubmissionValues"]),

    showSubmission(formSubmissionId, formId = this.formId) {
      this.$router.push(addToHash(this.$route.hash, FORM_SUBMISSIONS_HASH))
      this.wasOpenedDirectly = true
      this.chooseSubmission(formSubmissionId, formId)
    },

    shortTimeAgo(dateTime) {
      return timeAgo(dateTime)
        .replace("minute", "min")
        .replace("second", "sec")
    },

    chooseSubmission(formSubmissionId, formId = this.formId) {
      this.isLoadingSubmission = true

      this.formSubmissionId = formSubmissionId

      this.fetchFormSubmission({
        formId,
        formSubmissionId
      }).then(submission => {
        this.currentSubmission = submission
        this.isLoadingSubmission = false
      })
    },

    close() {
      this.$router.push(removeFromHash(this.$route.hash, FORM_SUBMISSIONS_HASH))
    },

    handleOpen() {
      this.key++
    }
  },

  watch: {
    "$route.hash": {
      immediate: true,
      handler: function(hash, prevHash) {
        const list = toHashList(hash)
        const prevList = toHashList(prevHash)

        const HASH_NAME = FORM_SUBMISSIONS_HASH.substring(1)

        if (!list.includes(HASH_NAME) && prevList.includes(HASH_NAME)) {
          this.currentSubmission = null
          this.wasOpenedDirectly = false
        }
      }
    }
  }
}
</script>

<style lang="scss">
.form-submissions-modal__back {
  opacity: 1 !important;

  &:disabled {
    transform: none;
  }
}

.form-submissions-modal__submission {
  padding-bottom: 6rem;
}
</style>
